// Password change modal component
// Allows users to change their password
// CHANGE: Added password visibility toggles with icons and text
// CHANGE: Added ReactDOM.createPortal to render modal at document root level for consistency
// CHANGE: Added password validation and requirements checklist
// CHANGE: Added dirty state detection and unsaved changes warning
// UPDATED: Migrated to centralized toast system for success/error messages
// UPDATED: Standardized icon size to 18 and button loading text to "En cours..."
// FIXED: Moved early return after all hooks to comply with Rules of Hooks

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Eye, EyeOff } from 'lucide-react';
import { getPortalRoot } from '../lib/portalUtils';
import { changePassword } from '../lib/auth';
import { ConfirmationDialog } from './ui/ConfirmationDialog';
import { validatePassword } from '../lib/passwordPolicy';
import { PasswordRequirementsChecklist } from './ui/PasswordRequirementsChecklist';
import { useToast } from '../hooks/useToast';

interface PasswordChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function PasswordChangeModal({ isOpen, onClose }: PasswordChangeModalProps) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
  const [success, setSuccess] = useState(false);
  const { showToast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(false);

    // Validate password confirmation
    if (newPassword !== confirmPassword) {
      showToast('Les mots de passe ne correspondent pas', 'error');
      return;
    }

    // Validate password against policy
    const validation = validatePassword(newPassword);
    if (!validation.isValid) {
      showToast(validation.errors[0], 'error');
      return;
    }

    // Show confirmation dialog
    setShowConfirmation(true);
  };

  const performPasswordChange = async () => {
    setLoading(true);
    const result = await changePassword(newPassword);

    if (result.success) {
      setSuccess(true);
      showToast('Mot de passe modifié avec succès', 'success');
      setTimeout(() => {
        onClose();
      }, 2000);
    } else {
      showToast(result.error || 'Une erreur est survenue', 'error');
    }

    setLoading(false);
  };

  const handleConfirm = async () => {
    setShowConfirmation(false);
    await performPasswordChange();
  };

  const hasUnsavedChanges = (): boolean => {
    // If password change was successful, no unsaved changes
    if (success) return false;

    // Check if any password field has been filled
    if (newPassword !== '') return true;
    if (confirmPassword !== '') return true;

    return false;
  };

  const handleCloseAttempt = () => {
    if (hasUnsavedChanges()) {
      setShowUnsavedChangesDialog(true);
    } else {
      onClose();
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !loading && !success) {
      if (!hasUnsavedChanges()) {
        onClose();
      }
    }
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading && !success) {
        if (showConfirmation) {
          setShowConfirmation(false);
        } else if (showUnsavedChangesDialog) {
          setShowUnsavedChangesDialog(false);
        } else {
          handleCloseAttempt();
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [loading, success, showConfirmation, showUnsavedChangesDialog, newPassword, confirmPassword]);

  if (!isOpen) return null;

  return createPortal(
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h3 className="text-xl font-bold text-gray-900">Changer le mot de passe</h3>
          <button
            onClick={handleCloseAttempt}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading || success}
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
              Nouveau mot de passe *
            </label>
            <div className="relative">
              <input
                id="newPassword"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                disabled={loading || success}
                className="w-full px-4 py-3 pr-32 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all disabled:bg-gray-50 disabled:cursor-not-allowed"
                aria-describedby="new-password-visibility-toggle"
              />
              <button
                type="button"
                id="new-password-visibility-toggle"
                onClick={() => setShowNewPassword(!showNewPassword)}
                disabled={loading || success}
                className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={showNewPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              >
                {showNewPassword ? (
                  <>
                    <EyeOff size={18} aria-hidden="true" />
                    <span>Masquer</span>
                  </>
                ) : (
                  <>
                    <Eye size={18} aria-hidden="true" />
                    <span>Afficher</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Password requirements checklist */}
          {newPassword && (
            <PasswordRequirementsChecklist password={newPassword} />
          )}

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
              Confirmer le mot de passe *
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading || success}
                className="w-full px-4 py-3 pr-32 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all disabled:bg-gray-50 disabled:cursor-not-allowed"
                aria-describedby="confirm-password-visibility-toggle"
              />
              <button
                type="button"
                id="confirm-password-visibility-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={loading || success}
                className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={showConfirmPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              >
                {showConfirmPassword ? (
                  <>
                    <EyeOff size={18} aria-hidden="true" />
                    <span>Masquer</span>
                  </>
                ) : (
                  <>
                    <Eye size={18} aria-hidden="true" />
                    <span>Afficher</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleCloseAttempt}
              disabled={loading || success}
              className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-all duration-200"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading || success}
              className="flex-1 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all duration-200 disabled:opacity-50"
            >
              {loading ? 'En cours...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showConfirmation}
        title="Confirmer le changement"
        message="Êtes-vous sûr de vouloir changer votre mot de passe ? Vous devrez utiliser le nouveau mot de passe lors de votre prochaine connexion."
        confirmLabel="Confirmer"
        cancelLabel="Annuler"
        onConfirm={handleConfirm}
        onCancel={() => setShowConfirmation(false)}
        variant="warning"
      />

      {/* Unsaved changes confirmation */}
      <ConfirmationDialog
        isOpen={showUnsavedChangesDialog}
        title="Modifications non enregistrées"
        message="Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter sans enregistrer ?"
        confirmLabel="Quitter sans enregistrer"
        cancelLabel="Continuer à modifier"
        onConfirm={onClose}
        onCancel={() => setShowUnsavedChangesDialog(false)}
        variant="warning"
      />
    </div>,
    getPortalRoot()
  );
}
